import { DateTime } from 'luxon';
import { combineActions, handleActions } from 'redux-actions';

import { resourceUpserters } from '../core';

import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';
import * as actions from './actions';
import { type CreditProfileStoreProps, type CreditProfileType, mkCreditProfileStoreType } from './types';

const creditProfileReducer = handleActions<
  QcsSyncResourceStore<CreditProfileType, CreditProfileStoreProps>,
  | ReturnType<typeof actions.createCreditProfile>
  | ReturnType<typeof actions.resendVerificationRetry>
  | ReturnType<typeof actions.pollRegisterCreditProfile>
  | ReturnType<typeof actions.registerCreditProfile>
  | ReturnType<typeof actions.pollRegisterCreditProfileSuccess>
  | ReturnType<typeof actions.resendVerificationRetrySuccess>
  | ReturnType<typeof actions.getCreditProfileSuccess>
  | ReturnType<typeof actions.getCreditProfile>
>(
  {
    [actions.createCreditProfile.type]: (state) => state.merge({ isLoading: true, attempts: 0, error: null }),

    [actions.resendVerificationRetry.type]: (state) =>
      state.merge({ isRetryLoading: true, attempts: (state.attempts || 0) + 1, lastRetryDate: DateTime.now().toISO() }),

    [actions.pollRegisterCreditProfile.type]: (state) => state.merge({ isPolling: true }),

    [actions.registerCreditProfile.type]: (state) => state.merge({ isContinuing: true }),

    [actions.pollRegisterCreditProfileSuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResources(state.merge({ isPolling: false, isContinuing: false }), payload),

    [actions.resendVerificationRetrySuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResources(state.merge({ isRetryLoading: false, isPolling: true }), payload),

    [actions.getCreditProfile.type]: (state) => state.merge({ isLoading: true }),

    [actions.getCreditProfileSuccess.type]: (state, { payload }) => resourceUpserters.upsertResources(state, payload),

    [combineActions(
      actions.createCreditProfileSuccess,
      actions.registerCreditProfileSuccess,
      actions.deleteCreditProfileSuccess,
    ).toString()]: (state: any, { payload }: any) =>
      resourceUpserters.upsertResource(
        state.merge({ isPolling: false, lastRetryDate: undefined, retryError: undefined }),
        payload,
      ),

    [combineActions(
      actions.getCreditProfileFailure,
      actions.createCreditProfileFailure,
      actions.deleteCreditProfileFailure,
    ).toString()]: (state: any, { payload }: any) =>
      resourceUpserters.completeWithError(state.merge({ isPolling: false }), payload),

    [combineActions(
      actions.resendVerificationRetryFailure,
      actions.registerCreditProfileFailure,
      actions.verifyCreditProfileFailure,
    ).toString()]: (state: any, { payload }: any) =>
      state.merge({ isPolling: false, isRetryLoading: false, isContinuing: false, retryError: payload }),
  },
  mkCreditProfileStoreType(),
);

export default creditProfileReducer;
